
import { Component, Vue, Watch, Prop } from "vue-property-decorator"

import VuePerfectScrollbar from "vue-perfect-scrollbar"
import vSelect from "vue-select"

import SkeletonLoader from "@/components/SkeletonLoader.vue"
import DataTable from "../DataTable.vue"

import api from "@/services/api"

import { Schedule, ScheduleFreq, FolderSchedules, SortByOptions, ScheduleStatus, ScheduleMessage, ScheduleType } from "@/types/schedule"
import cloneDeep from "lodash/cloneDeep"
import { CaseColumn } from "@/types/caseColumns"
import { TemplateDetails, TimeZone, Team } from "@/types"
import { formatDateMixin } from "@/mixins/formatDateMixin"

import CalendarView from "./CalendarView.vue"
import ScheduleSettings from "./ScheduleSettings.vue"
import ScheduleEditService from "@/services/scheduleEditService"

import apiScheduleSettings from "@/services/api_ScheduleSettings"

import moment from "moment"
import { CurrentUser } from "@/types/user"
import { Getter, State, Mutation } from "vuex-class"
import UserImage from "@/components/UserImage.vue"
import { FolderType, Folder } from "@/types/folder"
import _, { get } from "lodash"
import { UserSettings } from "@/types/userSettings"
import { TenantFeatures } from "@/types/tenantFeatures"

import { folderMixin } from "@/mixins/folderMixin"
import FolderService from "@/services/folder-service"

@Component({
	components: {
		"v-select": vSelect,
		scrollbar: VuePerfectScrollbar,
		"data-table": DataTable,
		skeleton: SkeletonLoader,
		"calendar-view": CalendarView,
		"schedule-settings": ScheduleSettings,
		"user-image": UserImage
	}
})
export default class ScheduleSetup extends Vue {
	$refs!: {
		dataTable: DataTable
	}

	@State tenantSettings!: { [key: string]: any } | null

	@Getter("getUserSettings")
	public userSettings!: UserSettings

	@Getter("getMe")
	public me!: CurrentUser;

	@Getter("getRecordName") getRecordName!: string;

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	@Getter("getTenantTimezones")
	public tenantTimeZones!: TimeZone[]

	@Mutation
	public setScheduleToastMessage!: (message: ScheduleMessage | null) => void

	folderService = new FolderService()

	private schedules: Schedule[] = [];
	private folderScheduleGroups: FolderSchedules[] = [];

	private loading: boolean = true
	private templates: TemplateDetails[] = [];
	private fields: CaseColumn[] = [];
	private generatingCase: boolean = false;

	private timeZones: TimeZone[] = []

	private showCalendar: boolean = false

	private showSettings: boolean = false

	private searchFilter: string = ""

	private folders: { [title:string]:Folder } = {}
	private folderList: Folder[] = []
	private currentSubFolderList: Folder[] = []
	private currentSubFolderListScheudleCheck: Folder[] = []
	private currentFolderSchedulesSubFolderList: any[] = []

	private teams: Team[] = []

	// If filterOutCaseColumns is updated please also update the ScheduleService Api
	private filterOutCaseColumns = ["created", "closed", "openForDays", "isClosed", "templateId", "caseNumber", "lastUpdated", "hiddenFromInsights", "deleted"]
	public selectedFields : any[] = [];

	private scheduleEditService = new ScheduleEditService()

	private sortByOptions: SortByOptions[] = this.getDefaultSortByOptions()

	private getDefaultSortByOptions () : SortByOptions[] {
		return [
			{
				columnKey: "status",
				descending: "asc"
			},
			{
				columnKey: "title",
				descending: "asc"
			}
		]
	}

	private get scheduleFreqOptions () {
		return [
			{
				title: "Once",
				id: ScheduleFreq.Once
			},
			{
				title: "Daily",
				id: ScheduleFreq.Daily
			},
			{
				title: "Weekly",
				id: ScheduleFreq.Weekly
			},
			{
				title: "Monthly",
				id: ScheduleFreq.Monthly
			},
			{
				title: "Custom Dates",
				id: ScheduleFreq.CustomDates
			}
		]
	}

	public get hasSettingsAuthorization () : boolean {
		return this.me && !!this.me.isAdmin
	}

	// Custom filter function to exclude folder rows from filtering
	private scheduleFilterFunction (row: any, filter: string): boolean {
		// Always show folder rows (they should never be filtered out)
		if (row.isFolderType) {
			return true
		}

		// If no filter, show all schedules
		if (!filter || filter.trim().length === 0) {
			return true
		}

		// For schedule rows, filter based on schedule properties
		const lowerFilter = filter.toLowerCase()

		// Check if row has schedule properties (name, title, description, etc.)
		if (row.name && row.name.toLowerCase().includes(lowerFilter)) {
			return true
		}
		if (row.title && row.title.toLowerCase().includes(lowerFilter)) {
			return true
		}
		if (row.description && row.description.toLowerCase().includes(lowerFilter)) {
			return true
		}
		if (row.status && typeof row.status === "string" && row.status.toLowerCase().includes(lowerFilter)) {
			return true
		}
		if (row.templateTitle && row.templateTitle.toLowerCase().includes(lowerFilter)) {
			return true
		}

		return false
	}

	private async loadFolders () {
		const refreshedFolders: Folder[] = await api.foldersGet(FolderType.Schedule)
		refreshedFolders.forEach(refreshedFolder => {
			const existingFolder = this.folders[refreshedFolder.title]
			if (existingFolder) {
				refreshedFolder.expanded = existingFolder.expanded
			} else {
				refreshedFolder.expanded = this.foldersExpandedByDefault
			}
		})

		this.folderList = refreshedFolders
		this.folders = refreshedFolders.toDictionary("title")
	}

	private folderDeleted (folder: Folder) {
		const folderSchedules = this.schedules.filter(schedule => schedule.folder === folder.title)
		folderSchedules.forEach(schedule => schedule.folder = null)
	}

	private async loadTeams () {
		const teams: Team[] = await api.teamsGet()
		this.teams = teams
	}

	private get scheduleColumns () {
		if (!this.scheduleEditService) return []
		return this.scheduleEditService.columns.filter((c: any) => {
			if (c.key === "endTime") {
				return !this.tenantSettings || !this.tenantSettings["scheduleHideTimeSpan"]
			}
			if (c.key === "bulkCreateForTeams") {
				return !this.tenantSettings || !this.tenantSettings["scheduleHideBulkCreate"]
			}
			if (c.key === "excludeWeekends") {
				return this.getIsFeatureEnabled(TenantFeatures.SchedulingExcludeWeekends)
			}
			if (c.key === "nextRunTime") {
				return this.getIsFeatureEnabled(TenantFeatures.ScheduleDisplayRunTimes)
			}
			return true
		})
	}

	private async mounted () {
		await this.load()

		this.initializeScheduleEditService()
	}

	private initializeScheduleEditService () {
		// data
		this.scheduleEditService.recordName = this.getRecordName
		this.scheduleEditService.tenantTimeZones = this.tenantTimeZones

		// tenant features
		this.scheduleEditService.getIsFeatureEnabled = this.getIsFeatureEnabled
		this.scheduleEditService.initializeFeatures()
	}

	private async load () {
		this.loading = true

		await this.scheduleEditService.load(this.me)
		await this.loadFolders()
		await this.loadTeams()
		await this.loadSchedules()
		await this.loadScheduleDefaults()

		this.loading = false
	}

	private async loadSchedules (): Promise<void> {
		const schedules = await api.retrieveSchedules()
		schedules.forEach((schedule: Schedule) => {
			schedule.status = this.determineScheduleStatus(schedule)
		})

		// Only show 'normal' Schedules
		this.schedules = schedules.filter(s => s.scheduleType === ScheduleType.Schedule || !s.scheduleType)

		const permittedFolders = this.scheduleEditService.getPermittedFolders()

		// Get all schedules that are either in a root folder or are not in a folder
		const folderScheduleGroups = _(this.schedules)
			.filter(c => !c.folder || ((permittedFolders[(c.folder)] !== undefined) && !permittedFolders[(c.folder)].parentFolderId))
			.groupBy(c => c.folder || "")
			.map((groupedSchedules: Schedule[], folderName: string) => {
				const hasNoFolder = folderName === null || folderName === undefined || folderName === ""

				const folderSchedules: FolderSchedules = {
					id: folderName,
					folderTitle: hasNoFolder ? "Uncategorized" : folderName,
					expanded: hasNoFolder || !!this.folders[folderMixin.methods.getFolderNameFromPath(folderName)]?.expanded,
					isFolderType: true,
					schedules: groupedSchedules,
					allowRowClick: true,
					onlyEmitOnRowClick: true,
					subFolders: []
				}

				// Reset the sub folder list
				this.currentSubFolderList = []

				var folder = folderMixin.methods.getFolderFromPath(folderName, this.folderList)
				if (folder) {
					if (this.subFolderContainsSchedule(folder)) {
						this.buildSubFolderList(folder.id)
						this.buildSubFolderSchedueList(folderSchedules, folder)
					}
				}

				return folderSchedules
			}).value()

		this.folderScheduleGroups = folderScheduleGroups

		// Add any empty root folders that have a sub folder containing a Schedule
		this.addValidEmptyRootFolders(permittedFolders)
	}

	// Loops through to recursively build up a list of sub folder schedules for the provided parent folder schedules
	buildSubFolderSchedueList (parentFolderSchedule: FolderSchedules, parentFolder: Folder) {
		// Filter the folders list to only include folders that have a parent
		const scheduleFoldersFiltered: Folder[] = this.folderList.filter(f => f.parentFolderId && f.parentFolderId !== null && f.id !== parentFolder.id)

		scheduleFoldersFiltered.forEach(folder => {
			// Get the full path to root for the current folder
			let folderPath: string = folderMixin.methods.getPathToRootFolder(folder, this.folderList, true)

			if (this.isDirectSubFolder(folderPath, parentFolder)) {
				// Get the list of schedules for the current folder
				var schedules = this.schedules.filter(s => !!s.folder && folderMixin.methods.getFolderNameFromPath(s.folder) === folder.title)

				// If the current folder contains a schedule, push it to be displayed
				if (this.schedules.some(v => v.folder === folderPath)) {
					const folderSchedules: FolderSchedules = {
						folderTitle: folderMixin.methods.getPathToRootFolder(folder, this.folderList, true),
						expanded: !!this.folders[folderMixin.methods.getFolderNameFromPath(folder.title)]?.expanded, // schedules with no folder are always expanded
						isFolderType: true,
						schedules: schedules,
						allowRowClick: true,
						onlyEmitOnRowClick: true,
						subFolders: [],
						parentFolderId: parentFolder.id
					}

					// Add the folder schedule to the parent's list of sub folders
					if (!parentFolderSchedule.subFolders) {
						parentFolderSchedule.subFolders = []
					}
					parentFolderSchedule.subFolders.push(folderSchedules)

					// Check if the folder schedule has any sub folders that need to be displayed
					this.buildSubFolderSchedueList(folderSchedules, folder)
				} else {
					// Check if the folder has any sub folders that contain a schedule
					if (this.subFolderContainsSchedule(folder)) {
						const folderSchedules: FolderSchedules = {
							folderTitle: folderMixin.methods.getPathToRootFolder(folder, this.folderList, true),
							expanded: !!this.folders[folderMixin.methods.getFolderNameFromPath(folder.title)]?.expanded, // schedules with no folder are always expanded
							isFolderType: true,
							schedules: schedules,
							allowRowClick: true,
							onlyEmitOnRowClick: true,
							subFolders: [],
							parentFolderId: parentFolder.id
						}

						// Add the folder schedule to the parent's list of sub folders
						if (!parentFolderSchedule.subFolders) {
							parentFolderSchedule.subFolders = []
						}
						parentFolderSchedule.subFolders.push(folderSchedules)

						// Check if the folder schedule has any sub folders that need to be displayed
						this.buildSubFolderSchedueList(folderSchedules, folder)
					}
				}
			}
		})
	}

	// Adds any root folders that do not directly contain a schedule, but have a sub folder that contains a schedule
	addValidEmptyRootFolders (permittedFolders: { [title: string]: Folder }) {
		const filteredFolderList = _(this.folderList)
			.filter(f => ((permittedFolders[(f.title)] !== undefined) && !permittedFolders[(f.title)].parentFolderId))

		filteredFolderList.forEach(folder => {
			if (!this.schedules.some(s => s.folder === folder.title) && this.subFolderContainsSchedule(folder)) {
				// Create folder schedule
				const folderSchedules: FolderSchedules = {
					folderTitle: folder.title,
					expanded: !!this.folders[folderMixin.methods.getFolderNameFromPath(folder.title)]?.expanded,
					isFolderType: true,
					schedules: [],
					allowRowClick: true,
					onlyEmitOnRowClick: true,
					subFolders: []
				}

				// Reset the sub folder list
				this.currentSubFolderList = []

				// Build the sub folder list for the current folder
				this.buildSubFolderList(folder.id)

				// Build the schedule lists for all of the current folder's sub folders
				this.buildSubFolderSchedueList(folderSchedules, folder)

				// Add the folder schedule to the main list of folder schedules
				this.folderScheduleGroups.push(folderSchedules)
			}
		})
	}

	// Builds up the list of sub folders and schedules
	private buildFolderSchedulesSubFolderList (folderSchedules: FolderSchedules) {
		// When filtering, treat all folders as expanded
		const hasActiveFilter = this.searchFilter && this.searchFilter.trim().length > 0

		if (folderSchedules.subFolders && folderSchedules.subFolders.length > 0) {
			// Loop through each sub folder, recursively getting any direct sub folders
			for (let i = 0, count = folderSchedules.subFolders.length; i < count; i++) {
				const subFolder = folderSchedules.subFolders[i]

				// Always add sub folder to the list
				this.currentFolderSchedulesSubFolderList.push(subFolder)

				// Add schedules if the subfolder is expanded or filter is active
				if (subFolder.expanded || hasActiveFilter) {
					const scheduleList: Schedule[] = []

					subFolder.schedules.forEach(schedule => {
						scheduleList.push(schedule)
					})

					// Order the list of schedules and push it to the master list
					const scheduleListCopy: Schedule[] = _(scheduleList).orderBy(this.getScheduleOrdering, this.getScheduleOrderDirection).value()
					this.currentFolderSchedulesSubFolderList.push(...scheduleListCopy)
				}

				// Recursively get direct sub folders of current sub folder
				this.buildFolderSchedulesSubFolderList(subFolder)
			}
		}
	}

	public get expandedRows () : (FolderSchedules | Schedule)[] {
		// When filtering/searching, show all schedules regardless of folder expansion state
		const hasActiveFilter = this.searchFilter && this.searchFilter.trim().length > 0

		const expandedRows = _(this.folderScheduleGroups)
			.orderBy([(item: FolderSchedules) => { return item.folderTitle }])
			.map(folderGroup => {
				const rootFolderGroup = folderGroup
				const rootFolderSchedules = folderGroup.schedules

				// Reset the list of sub folders
				this.currentFolderSchedulesSubFolderList = []

				if (folderGroup.subFolders && folderGroup.subFolders.length > 0) {
					// Sub folders exist - always build up the list of sub folders
					this.buildFolderSchedulesSubFolderList(folderGroup)
				}

				// Always show folders in structure
				if (folderGroup.subFolders && folderGroup.subFolders.length > 0) {
					// Always show root folder and subfolder structure
					const result: (FolderSchedules | Schedule)[] = [rootFolderGroup]
					// Add parent's own schedules only if parent is expanded or filtering
					if (folderGroup.expanded || hasActiveFilter) {
						result.push(..._(rootFolderSchedules).orderBy(this.getScheduleOrdering, this.getScheduleOrderDirection).value())
					}

					// Always add subfolder structure (subfolders show their own schedules based on their expansion state)
					result.push(..._(this.currentFolderSchedulesSubFolderList).value())

					return result
				} else {
					// No subfolders - show folder and schedules if expanded or filtering
					if (folderGroup.expanded || hasActiveFilter) {
						return [folderGroup, ..._(folderGroup.schedules).orderBy(this.getScheduleOrdering, this.getScheduleOrderDirection).value()]
					} else {
						return [folderGroup]
					}
				}
			}).flatten()
			.value()

		return expandedRows
	}

	// Checks if the provided folder name is a direct sub folder of the provided parent
	private isDirectSubFolder (folderName: string, parentFolder: Folder): boolean {
		const folder = folderMixin.methods.getFolderFromPath(folderName, this.folderList)

		if (!folder) return false

		if (this.folderList.firstOrDefault(f => f.title === parentFolder.title && f.id === folder.parentFolderId)) {
			return true
		} else {
			return false
		}
	}

	subFolderContainsSchedule (folder: Folder) {
		// Rebuild the sub folder list for the current folder
		this.currentSubFolderListScheudleCheck = []
		this.buildSubFolderListScheduleCheck(folder.id)

		// Folder has no sub folders
		if (this.currentSubFolderListScheudleCheck.length < 1) {
			return false
		}

		// Check if any sub folders contain a Schedule
		if (this.schedules.some(v => this.currentSubFolderListScheudleCheck.find(f => folderMixin.methods.getPathToRootFolder(f, this.folderList, true) === v.folder))) {
			return true
		} else {
			return false
		}
	}

	buildSubFolderListScheduleCheck (folderId: string) {
		this.buildSubFolderListToArray(folderId, item => this.currentSubFolderListScheudleCheck.push(item))
	}

	buildSubFolderList (folderId: string) {
		this.buildSubFolderListToArray(folderId, item => this.currentSubFolderList.push(item))
	}

	buildSubFolderListToArray (folderId: string, addToList: (subFolder: any) => void) {
		// Create list of direct sub folders of provided folder ID
		let subFolderList: Folder[] = this.folderList.filter(f => f.parentFolderId === folderId)
		subFolderList.forEach(item => {
			addToList(item)
			this.buildSubFolderListToArray(item.id, addToList)
		})
	}

	public get foldersExpandedByDefault () {
		if (this.userSettings &&
			this.userSettings.settings &&
			this.userSettings.settings["scheduleFoldersExpanded"] !== null &&
			this.userSettings.settings["scheduleFoldersExpanded"] !== undefined) {
			return !!this.userSettings.settings["scheduleFoldersExpanded"]
		}
		return true
	}

	private loadScheduleItem (schedule : any) {
		let scheduleItem = cloneDeep(schedule)

		this.scheduleEditService.initialTemplateId = cloneDeep(scheduleItem.templateId)
		this.scheduleEditService.currentSchedule = scheduleItem

		return scheduleItem
	}

	private async loadScheduleDefaults (): Promise<void> {
		let scheduleSettings = await apiScheduleSettings.ScheduleSettingsList()
		if (scheduleSettings) {
			Vue.set(this.scheduleEditService, "scheduleSetting", scheduleSettings)
		}
	}

	private async deleteSchedule (schedule: Schedule): Promise<void> {
		await this.scheduleEditService.deleteSchedule(schedule)
		await this.loadSchedules()
	}

	private async saveSchedule (schedule: Schedule): Promise<void> {
		if (this.getIsFeatureEnabled(TenantFeatures.ScheduleForecastPopup)) {
			const toastTitle = (/schedule/i).test(schedule.title) ? schedule.title : `${schedule.title} Schedule`
			this.setScheduleToastMessage(
				{
					scheduleId: schedule.title,
					message: `Forecasting ${this.getRecordName}s for ${toastTitle}`,
					variant: "info",
					autoClose: false
				})
		}
		const data = await this.scheduleEditService.saveSchedule(schedule)
		await this.loadSchedules()
	}

	private createSchedule (values: any) {
		const ianaTimezoneValue = Intl.DateTimeFormat().resolvedOptions().timeZone
		const timeZone = this.timeZones.find(t => t.iana === ianaTimezoneValue)

		var newSchedule = {
			timeZoneId: timeZone ? timeZone.id : null,
			fields: {},
			folder: (!!values && values.folderTitle && values.folderTitle !== "Uncategorized") ? values.folderTitle : null,
			templateId: null,
			recordsToCreate: 1,
			...(values || {})
		}

		if (this.scheduleHideStartTimeFeatureIsEnabled) {
			newSchedule.startTime = "00:00"
			newSchedule.endTime = null
		}

		this.scheduleEditService.initialTemplateId = null
		this.scheduleEditService.currentSchedule = newSchedule

		return newSchedule
	}

	@Watch("scheduleEditService.currentSchedule.timeZoneId")
	private timeZoneChanged (selectedTimeZone: string) {
		this.scheduleEditService.timeZoneChanged(selectedTimeZone)
	}

	@Watch("scheduleEditService.currentSchedule.templateId")
	private templateChanged (selectedTemplateId: string) {
		var applyDefaultValues = selectedTemplateId !== this.scheduleEditService.initialTemplateId

		this.scheduleEditService.templateChanged(selectedTemplateId, applyDefaultValues)
		this.scheduleEditService.initialTemplateId = selectedTemplateId
	}

	@Watch("scheduleEditService.currentSchedule.minRunDate")
	private minRunDateChanged (selectedDate: Date) {
		this.scheduleEditService.minRunDateChanged(selectedDate)
	}

	@Watch("scheduleEditService.currentSchedule.maxRunDate")
	private maxRunDateChanged (selectedDate: Date) {
		this.scheduleEditService.maxRunDateChanged(selectedDate)
	}

	@Watch("scheduleEditService.currentSchedule.scheduleFreq")
	private scheduleFrequencyChanged (scheduleFrequency: any) {
		this.scheduleEditService.scheduleFrequencyChanged(scheduleFrequency)
	}

	@Watch("scheduleEditService.currentSchedule.dayOf")
	private scheduleDayOfChanged (dayOf: number) {
		this.scheduleEditService.scheduleDayOfChanged(dayOf)
	}

	public saveSelectedFields (event: any) {
		this.selectedFields = event
	}

	private async TriggerGeneration (schedule: any) {
		try {
			this.generatingCase = true
			const createdCases = await api.triggerSchedule(schedule)

			createdCases.forEach(createdCase => {
				this.$bvToast.toast(`Created ${createdCase.caseNumber}`, {
					title: "Created",
					variant: "success",
					solid: true,
					autoHideDelay: 5000
				})
			})
		} catch (error) {
			console.log(error)
			this.$bvToast.toast("There was a problem triggering the schedule", {
				title: "Schedule Error",
				variant: "danger",
				solid: true,
				autoHideDelay: 5000
			})
		} finally {
			// wait 2 seconds before enabling the trigger again
			setTimeout(() => { this.generatingCase = false }, 2000)
		}
	}

	private toggleExpandFolder (folder: any) {
		if (!folder.isFolderType) {
			return
		}
		const folderRow: FolderSchedules = folder
		folderRow.expanded = !folderRow.expanded
	}

	public get getScheduleOrdering () : ((item: Schedule) => any)[] {
		return this.sortByOptions.map((sortByOption) => {
			return (item: Schedule) => { return get(item, sortByOption.columnKey, 0) }
		})
	}

	public get getScheduleOrderDirection () : ("asc" | "desc")[] {
		return this.sortByOptions.map((sortByOption) => {
			return sortByOption.descending
		})
	}

	private sortChanged (context: any) {
		if (!context || !context.sortBy) {
			this.sortByOptions = this.getDefaultSortByOptions()
		}

		const descOption = context.sortDesc ? "desc" : "asc"

		this.sortByOptions = [{
			columnKey: context.sortBy,
			descending: descOption
		}]
	}

	private rowClass (item: any, type: any): string {
		if (!item || type !== "row") return ""
		if (item.isFolderType) return "folder-row"

		const schedule = item as Schedule

		if (schedule.status === ScheduleStatus.Closed) {
			return "muted-row"
		}
		return ""
	}

	private determineScheduleStatus (schedule: Schedule) : ScheduleStatus {
		if (schedule.scheduleFreq === ScheduleFreq.Once && moment().utc().startOf("day") < moment(schedule.date)) {
			return ScheduleStatus.Upcoming
		}

		if (schedule.minRunDate && moment().utc().startOf("day") < moment(schedule.minRunDate)) {
			return ScheduleStatus.Upcoming
		}

		if (schedule.maxRunDate && moment().utc().startOf("day") > moment(schedule.maxRunDate)) {
			return ScheduleStatus.Closed
		}
		return ScheduleStatus.Open
	}

	private createScheduleFromRow (item: any) {
		const folderIdentifier = item.id ?? (item.folderTitle !== "Uncategorized" ? item.folderTitle : null)
		this.$refs.dataTable.createNew({
			folder: folderIdentifier
		})
	}

	private friendlyFolderName (schedule: Schedule) {
		if (schedule.folder === null || schedule.folder === undefined) {
			return "None"
		}
		return schedule.folder
	}

	private folderPathName (folderTitle: string) {
		if (this.folderList.filter(f => f.title === folderTitle).length === 0) {
			return folderTitle
		}

		const folder: Folder = folderMixin.methods.getFolderFromPath(folderTitle, this.folderList)
		return folderMixin.methods.getPathToRootFolder(folder, this.folderList, true)
	}

	get scheduleHideStartTimeFeatureIsEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.ScheduleHideStartTime)
	}
}

